import React, { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { updatePaymentMethod, loadPolicyDetails } from '../../../actions';

import {
  useWebViewReskin,
  useResponsive,
  usePaymentech,
  useMoneris,
} from '../../../hooks';
import { MONERIS_MARKET_CHANNELS, PAYMENTECH_MARKET_CHANNELS } from './billingAndPayments.constants';

import BillingDetailsCard from './BillingDetailsCard';
import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';
import PaymentMethodCard from './PaymentMethodCard';
import {
  EditPaymentMultiPolicyModal,
} from '../../settings/EditPaymentMultiPolicyModal';
import { PaymentMethodCA } from './PaymentMethodCA';
import { PaymentechIframe } from './BillingAndPaymentsDetails/PaymentechIframe';

import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';
import { ENTRY_POINT } from '../../../services/mixpanel.constants';

import style from './css/BillingInfoScreen.module.css';

const BillingInfoScreen = ({
  entryPoint = ENTRY_POINT.myAccount,
  policyNumber = '',
  policyId = 0,
}) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  const useMobileReskin = useWebViewReskin();
  const { isMobile } = useResponsive();
  const usePaymentechFlag = usePaymentech();
  const useMonerisFlag = useMoneris();
  const store = useSelector(({ billingPayments, policies }) => ({
    billingPayments,
    policies,
  }), shallowEqual);

  const {
    billingPayments: {
      policyDetails,
      updatingPaymentMethod,
    },
  } = store;
  const { allBrandsData, loadingAllBrandsData } = store.billingPayments;
  const isLoading = updatingPaymentMethod || loadingAllBrandsData;

  const policyInformation = useMemo(() => {
    if (loadingAllBrandsData) {
      return {};
    }

    // distinguish between different policy id (event with
    // the same policy number) for Orca companies.
    return allBrandsData.find((policy) => policy.PolicyId === Number(policyId))
      || allBrandsData[0];
  }, [allBrandsData, loadingAllBrandsData, policyId]);

  const marketingChannelId = useMemo(
    () => policyInformation?.MarketingChannelId,
    [policyInformation?.MarketingChannelId],
  );

  const onUpdatePaymentMethod = ({
    paymentMethodData,
    policies,
    sameBillingAddress,
    userInformation,
  }) => {
    const stripePaymentData = paymentMethodData;

    dispatch(updatePaymentMethod({
      policies,
      sameBillingAddress,
      stripePaymentData,
      userInformation,
    }));
  };

  const renderPaymentCard = () => {
    let paymentCard = null;

    if (MONERIS_MARKET_CHANNELS.includes(marketingChannelId)) {
      const { BillingPayments } = policyInformation;

      paymentCard = useMonerisFlag ? (
        <PaymentMethodCA
          entryPoint={entryPoint}
          marketingChannelId={marketingChannelId}
          paymentMethod={BillingPayments?.PaymentMethod}
          PolicyId={policyInformation?.PolicyId}
          type={BillingPayments?.Type}
        />
      ) : null;
    } else if (PAYMENTECH_MARKET_CHANNELS.includes(marketingChannelId)) {
      paymentCard = usePaymentechFlag ? (
        <PaymentechIframe
          entryPoint={entryPoint}
          marketingChannelId={marketingChannelId}
          paymentMethod={policyInformation?.BillingPayments?.PaymentMethod}
          paymentType={policyInformation?.BillingPayments?.Type}
          policyIdFigo={policyInformation?.PolicyId}
        />
      ) : null;
    } else {
      paymentCard = (
        <PaymentMethodCard
          diamondPolicyId={policyDetails?.PolicyId}
          onUpdatePaymentMethod={onUpdatePaymentMethod}
          policyInformation={policyInformation}
          policyNumber={policyNumber}
        />
      );
    }

    if (!paymentCard) {
      return null;
    }

    return (
      <>
        <h1
          className={useMobileReskin ? style.billingInfoScreenTitleResponsive
            : style.billingInfoScreenTitle}
        >
          {t('billingDetailsCard.paymentMethod')}
        </h1>

        {paymentCard}
      </>
    );
  };

  useEffect(() => {
    if (policyNumber && marketingChannelId === MARKETING_CHANNEL_ID.figo) {
      dispatch(loadPolicyDetails(policyNumber));
    }
  }, [policyNumber, marketingChannelId]);

  return (
    <div className={style.billingInfoScreenContainer}>
      {!useMobileReskin && isMobile ? (
        <h1 className={style.billingInfoScreenTitle}>
          {t('billingDetailsCard.title')}
        </h1>
      ) : null}

      <EditPaymentMultiPolicyModal
        onUpdatePaymentMethod={onUpdatePaymentMethod}
      />

      <FigoLoadingOverlay visible={isLoading} />

      <BillingDetailsCard
        marketingChannelId={marketingChannelId}
        policyInformation={policyInformation}
      />

      {renderPaymentCard()}
    </div>
  );
};

export default BillingInfoScreen;
